import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Key, BookOpen, TrendingUp, Award, Feather, PenLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import CofreIdentidade from '../components/CofreIdentidade';
import EvolucaoLiteraria from '../components/EvolucaoLiteraria';
import FrasesInspiradoras from '../components/FrasesInspiradoras';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [dailyKey, setDailyKey] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [keyRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/daily-key`, { withCredentials: true }),
          axios.get(`${API_URL}/api/stats/dashboard`, { withCredentials: true }),
        ]);
        setDailyKey(keyRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Check if user has written any chapters
  const hasWrittenContent = stats && (
    stats.total_words_all_time > 0 || 
    (stats.current_book_progress && stats.current_book_progress.chapters_count > 0)
  );

  const handleContinueWriting = async () => {
    // Check for last edited chapter in localStorage
    const lastEditedChapter = localStorage.getItem('lastEditedChapter');
    if (lastEditedChapter) {
      navigate(`/editor/${lastEditedChapter}`);
      return;
    }

    // If current book exists with chapters, go to the last chapter
    if (stats?.current_book_progress && stats.current_book_progress.chapters_count > 0) {
      try {
        const chaptersRes = await axios.get(
          `${API_URL}/api/books/${stats.current_book_progress.book_id}/chapters`,
          { withCredentials: true }
        );
        if (chaptersRes.data.length > 0) {
          // Go to the last chapter
          const lastChapter = chaptersRes.data[chaptersRes.data.length - 1];
          navigate(`/editor/${lastChapter.chapter_id}`);
          return;
        }
      } catch (error) {
        console.error('Error fetching chapters:', error);
      }
      // Fallback to book page
      navigate(`/livro/${stats.current_book_progress.book_id}`);
    } else if (stats?.current_book_progress) {
      // Book exists but no chapters
      navigate(`/livro/${stats.current_book_progress.book_id}`);
    } else {
      // No books yet, create first book
      navigate('/novo-livro');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="dashboard-page">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-3xl md:text-5xl font-light mb-2">Progresso Criativo</h1>
            <p className="text-muted-foreground text-sm md:text-base">Seu espaço de criação literária</p>
          </div>

          {/* Chave do Dia */}
          {dailyKey && (
            <div 
              className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-2 border-amber-500/20 rounded-lg p-8"
              data-testid="daily-key-block"
            >
              <div className="flex items-center gap-3 mb-4">
                <Key className="w-8 h-8 text-amber-500" />
                <h2 className="text-2xl font-serif">
                  {dailyKey.used_today ? 'Chave de Hoje Usada' : 'Chave do Dia'}
                </h2>
              </div>
              
              {dailyKey.used_today ? (
                <div className="space-y-4">
                  <p className="text-lg leading-relaxed">
                    Você abriu a porta da escrita hoje.
                  </p>
                  <p className="text-2xl font-medium text-amber-600 dark:text-amber-400">
                    Palavras escritas: {dailyKey.words_today}
                  </p>
                  <Button 
                    onClick={handleContinueWriting}
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                    data-testid="continue-writing-button"
                  >
                    <PenLine className="mr-2" size={18} />
                    Continuar de onde parou
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-lg leading-relaxed italic">
                    "{dailyKey.phrase}"
                  </p>
                  <Button 
                    onClick={handleContinueWriting}
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                    data-testid="start-writing-button"
                  >
                    <PenLine className="mr-2" size={18} />
                    {hasWrittenContent ? 'Continuar de onde parou' : 'Começar a escrever'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stats Grid - Only show after first chapter is written */}
          {hasWrittenContent ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Evolução da Obra */}
              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-6 h-6 text-amber-500" />
                  <h3 className="text-xl font-serif">Evolução da Obra</h3>
                </div>
                {stats.current_book_progress ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Livro atual:</p>
                      <p className="text-lg font-medium">{stats.current_book_progress.title}</p>
                    </div>
                    <div className="flex justify-center gap-12 pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {stats.current_book_progress.total_words.toLocaleString('pt-BR')}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">palavras</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {stats.current_book_progress.chapters_count}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">capítulos</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">Nenhum livro em andamento.</p>
                )}
              </div>

              {/* Ritmo Recente */}
              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-6 h-6 text-amber-500" />
                  <h3 className="text-xl font-serif">Ritmo Recente</h3>
                </div>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-amber-600 dark:text-amber-400">
                      {stats.words_this_week.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">palavras na última semana</p>
                  </div>
                  <div className="flex justify-center gap-8 pt-2">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{stats.best_day_words}</p>
                      <p className="text-xs text-muted-foreground mt-1">melhor dia de escrita</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{stats.streak_days}</p>
                      <p className="text-xs text-muted-foreground mt-1">dias seguidos escrevendo</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seu Legado de Escrita */}
              <div className="bg-card border border-border/40 rounded-lg p-6 md:col-span-2">
                <div className="flex items-center gap-2 mb-6">
                  <Award className="w-6 h-6 text-amber-500" />
                  <h3 className="text-xl font-serif">Seu Legado de Escrita</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{stats.total_books}</p>
                    <p className="text-sm text-muted-foreground mt-1">total de livros</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.finished_books}</p>
                    <p className="text-sm text-muted-foreground mt-1">finalizados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.published_books}</p>
                    <p className="text-sm text-muted-foreground mt-1">publicados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.books_in_progress}</p>
                    <p className="text-sm text-muted-foreground mt-1">em andamento</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{stats.total_words_all_time.toLocaleString('pt-BR')}</p>
                    <p className="text-sm text-muted-foreground mt-1">palavras escritas</p>
                  </div>
                </div>
              </div>

              {/* Indicadores Criativos - Fase 2 */}
              <div className="md:col-span-2">
                <div className="grid md:grid-cols-2 gap-6">
                  <CofreIdentidade />
                  <EvolucaoLiteraria showHistory={true} />
                </div>
              </div>
            </div>
          ) : (
            /* Empty state - before first chapter */
            <div className="bg-card border border-border/40 rounded-lg p-8 text-center">
              <Feather className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg text-muted-foreground">
                Sua jornada de escrita começará a aparecer aqui após seu primeiro capítulo.
              </p>
            </div>
          )}

          {/* Footer Quote */}
          <div className="text-center py-8 border-t border-border/40">
            <Feather className="w-8 h-8 mx-auto mb-3 text-amber-500" />
            <p className="text-sm italic text-muted-foreground">
              "Toda grande revolução começa com um sonho escrito."
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
